// src/components/kuestion/solo/soloData.js
// Small readers over the existing single-user endpoints, shared by the Solo
// home, history and exam screens.
import { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useKuestionApi } from '../useKuestionApi';

// A sitting's verdict comes from the threshold snapshotted when it started
// (contract v4 §6): `pass_percentage` 0–100 or null, `passed` true/false/null.
// Nothing here invents a pass mark when the server sends none.
export const verdictOf = (s) => {
  if (!s || s.passed == null) return null;
  return s.passed ? 'pass' : 'fail';
};

// History rows arrive with scored/total_questions (and sometimes percentage).
export const normaliseSitting = (h, i) => {
  const total = Number(h.total ?? h.total_questions ?? 0);
  const scored = Number(h.scored ?? h.score ?? 0);
  const pct = h.percentage != null ? Number(h.percentage) : h.percent != null ? Number(h.percent) : (total ? (scored / total) * 100 : 0);
  const passPct = h.pass_percentage != null ? Number(h.pass_percentage) : null;
  const passed = h.passed != null ? Boolean(h.passed) : (passPct != null ? pct >= passPct : null);
  return {
    id: h.id ?? h.session_id ?? i,
    exam_name: h.exam_name || h.template_name || 'Exam',
    total,
    scored,
    pct: Math.round(pct * 10) / 10,
    pass_percentage: passPct,
    passed,
    time_taken: Number(h.time_taken ?? 0),
    at: h.completed_at || h.submitted_at || h.created_at || null,
    modules: h.modules,
  };
};

export function useSoloData() {
  const api = useKuestionApi();
  const [templates, setTemplates] = useState(null);
  const [files, setFiles] = useState(null);
  const [history, setHistory] = useState(null);
  const [strengths, setStrengths] = useState(null);

  const reload = useCallback(() => {
    let live = true;
    api.get(API_ENDPOINTS.KQ_TEMPLATES).then((d) => live && setTemplates(d || [])).catch(() => live && setTemplates([]));
    api.get(API_ENDPOINTS.KQ_FILE_LIST).then((d) => live && setFiles(d || [])).catch(() => live && setFiles([]));
    api.get(API_ENDPOINTS.KQ_EXAM_HISTORY)
      .then((d) => live && setHistory((Array.isArray(d) ? d : (d?.history || d?.sessions || [])).map(normaliseSitting)))
      .catch(() => live && setHistory([]));
    api.get(API_ENDPOINTS.KQ_ANALYTICS_ME).then((d) => live && setStrengths(d || { modules: [] })).catch(() => live && setStrengths({ modules: [] }));
    return () => { live = false; };
  }, [api]);

  useEffect(() => reload(), [reload]);

  return { api, templates, files, history, strengths, reload };
}
